import React from "react";
import {
  deviceHasIpOnRecord,
  formatBatteryLevelText,
  formatNullableBoolean,
  formatOptionalPayloadCacheColumnValue,
  isDeviceInKiosk,
  isDeviceNotAllowed,
  readDeviceIsPlugged,
  readDevicePayloadField,
} from "../../functions/mdmDeviceDisplayUtils";
import {
  ColumnFilterControl,
  ColumnSortButton,
  DateRangeFilterPanel,
  DEVICE_CHARGING_OPTIONS,
  DEVICE_HAS_IP_OPTIONS,
  DEVICE_IN_KIOSK_OPTIONS,
  DEVICE_STATUS_OPTIONS,
  DropdownFilterPanel,
  RangeFilterPanel,
  SearchFilterPanel,
} from "./DeviceFilters";
import MdmDeviceNotesModal from "./MdmDeviceNotesModal";

const DeviceNotesCell = ({ deviceId, notes, canEdit, isSaving, onSave, isNotAllowed = false }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const displayText = notes == null || notes === "" ? null : String(notes);

  const handleOpen = () => {
    if (!canEdit || isSaving) return;
    setIsModalOpen(true);
  };

  return (
    <>
      {canEdit ? (
        <button
          type="button"
          className={
            isNotAllowed
              ? "w-full min-w-[10rem] max-w-[16rem] min-h-[2.5rem] text-left text-xs bg-white/10 border border-white/30 rounded-md px-2 py-1 transition-colors hover:bg-white/20 disabled:opacity-60 disabled:cursor-not-allowed"
              : "w-full min-w-[10rem] max-w-[16rem] min-h-[2.5rem] text-left text-xs bg-surface-container-low border border-outline-variant/30 rounded-md px-2 py-1 transition-colors hover:border-primary/40 hover:bg-surface-container disabled:opacity-60 disabled:cursor-not-allowed"
          }
          onClick={handleOpen}
          disabled={isSaving}
          aria-label="Edit device notes"
        >
          {displayText ? (
            <span className={`line-clamp-2 whitespace-pre-wrap break-words ${isNotAllowed ? "text-white" : "text-on-surface-variant"}`}>{displayText}</span>
          ) : (
            <span className={`italic ${isNotAllowed ? "text-white/70" : "text-on-surface-variant/60"}`}>Add notes...</span>
          )}
        </button>
      ) : (
        <div
          className={
            isNotAllowed
              ? "w-full min-w-[10rem] max-w-[16rem] min-h-[2.5rem] text-xs text-white bg-white/10 border border-white/30 rounded-md px-2 py-1"
              : "w-full min-w-[10rem] max-w-[16rem] min-h-[2.5rem] text-xs text-on-surface-variant bg-surface-container-low border border-outline-variant/30 rounded-md px-2 py-1"
          }
        >
          {displayText ? (
            <span className="line-clamp-2 whitespace-pre-wrap break-words">{displayText}</span>
          ) : (
            <span className={`italic ${isNotAllowed ? "text-white/70" : "text-on-surface-variant/60"}`}>No notes</span>
          )}
        </div>
      )}
      <MdmDeviceNotesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        deviceId={deviceId}
        notes={notes}
        isSaving={isSaving}
        onSave={onSave}
      />
    </>
  );
};

const BatteryLevelCell = ({
  level,
  getBatteryIcon,
  considerPluggedStatus = false,
  isPlugged,
  isNotAllowed = false,
}) => {
  const isLow = Number(level) <= 15;
  const useRedIcon = considerPluggedStatus ? isLow || isPlugged !== true : isLow;

  return (
    <div className="flex items-center">
      <span
        className={
          isNotAllowed
            ? "material-symbols-outlined text-white mr-2 text-sm"
            : useRedIcon
              ? "material-symbols-outlined text-error mr-2 text-sm"
              : "material-symbols-outlined text-green-600 mr-2 text-sm"
        }
      >
        {getBatteryIcon(level)}
      </span>
      <span className={`font-medium ${isNotAllowed ? "text-white" : "text-on-surface"}`}>
        {formatBatteryLevelText(level)}
      </span>
    </div>
  );
};

const MdmDevicesTable = ({
  allDisplayedSelected,
  isSelectAllIndeterminate = false,
  toggleAllDisplayedSelection,
  openColumnFilter,
  toggleColumnFilter,
  deviceFilters,
  setDeviceFilters,
  closeColumnFilters,
  sortField,
  sortDirection,
  handleColumnSortToggle,
  setDropdownFilterValue,
  setRangeFilterValue,
  setDateRangeFilterValue,
  isSearchFilterActive,
  setSearchFilterValue,
  activeManagedColumnKeys,
  isDateFilterActive,
  isBatteryFilterActive,
  isStatusFilterActive,
  isHasIpFilterActive,
  isChargingFilterActive,
  isInKioskFilterActive,
  isCurrentFolderFilterActive,
  activeOptionalTimeColumns,
  activeOptionalPayloadCacheColumns,
  isAllDevicesView,
  isLoading,
  fetchError,
  tableColSpan,
  displayedDevices,
  totalDevices,
  getStatus,
  cleanSsid,
  selectedDevices,
  handleDeviceSelectionClick,
  formatTime,
  openDeviceInfoJsonModal,
  getBatteryIcon,
  getDuplicateFolderPath,
  getCurrentFolderName,
  currentFolderFilterOptions = [],
  deviceActionResults,
  canEditDeviceNotes = false,
  savingNotesDeviceIds = {},
  onDeviceNotesSave,
}) => {
  const selectAllCheckboxRef = React.useRef(null);

  React.useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isSelectAllIndeterminate;
    }
  }, [isSelectAllIndeterminate, allDisplayedSelected]);

  return (
  <div className="overflow-auto max-h-[65vh] border-b border-outline-variant/10">
    <table className="w-full text-left border-separate border-spacing-y-0.5 px-4">
      <thead className="bg-surface-container-lowest sticky top-0 z-10">
        <tr>
          <th className="py-4 px-4 w-10">
            <input
              ref={selectAllCheckboxRef}
              className="rounded border-outline-variant text-primary focus:ring-primary/20 h-4 w-4"
              type="checkbox"
              checked={allDisplayedSelected}
              onChange={toggleAllDisplayedSelection}
            />
          </th>
          <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
            <div className="inline-flex items-center gap-1">
              <ColumnFilterControl
                label="Device Name"
                isOpen={openColumnFilter === "searchText"}
                isActive={isSearchFilterActive}
                onToggle={() => toggleColumnFilter("searchText")}
              >
                <SearchFilterPanel
                  value={deviceFilters.searchText}
                  placeholder="Search by device name or ID"
                  onChange={setSearchFilterValue}
                  onClear={() => setSearchFilterValue("")}
                  onDone={closeColumnFilters}
                />
              </ColumnFilterControl>
              <ColumnSortButton
                label="Device Name"
                isActive={sortField === "deviceName"}
                direction={sortDirection}
                onClick={() => handleColumnSortToggle("deviceName")}
              />
            </div>
          </th>
          {activeManagedColumnKeys.includes("agentCheckIn") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="Agent Check-in"
                  isOpen={openColumnFilter === "agentCheckInDate"}
                  isActive={isDateFilterActive}
                  onToggle={() => toggleColumnFilter("agentCheckInDate")}
                >
                  <DateRangeFilterPanel
                    value={deviceFilters.agentCheckInDate}
                    onChange={(key, value) => setDateRangeFilterValue("agentCheckInDate", key, value)}
                    onClear={() =>
                      setDeviceFilters((prev) => ({
                        ...prev,
                        agentCheckInDate: { start: "", end: "" },
                      }))
                    }
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="Agent Check-in"
                  isActive={sortField === "agentCheckIn"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("agentCheckIn")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("battery") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="Battery"
                  isOpen={openColumnFilter === "batteryLevel"}
                  isActive={isBatteryFilterActive}
                  onToggle={() => toggleColumnFilter("batteryLevel")}
                >
                  <RangeFilterPanel
                    value={deviceFilters.batteryLevel}
                    minPlaceholder="Min (0)"
                    maxPlaceholder="Max (100)"
                    onChange={(key, value) => setRangeFilterValue("batteryLevel", key, value)}
                    onClear={() =>
                      setDeviceFilters((prev) => ({
                        ...prev,
                        batteryLevel: { min: "0", max: "100" },
                      }))
                    }
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="Battery"
                  isActive={sortField === "battery"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("battery")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("chargingStatus") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="Charging"
                  isOpen={openColumnFilter === "chargingStatus"}
                  isActive={isChargingFilterActive}
                  onToggle={() => toggleColumnFilter("chargingStatus")}
                >
                  <DropdownFilterPanel
                    value={deviceFilters.chargingStatus}
                    options={DEVICE_CHARGING_OPTIONS}
                    onChange={(values) => setDropdownFilterValue("chargingStatus", values)}
                    onClear={() => setDropdownFilterValue("chargingStatus", [])}
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="Charging"
                  isActive={sortField === "chargingStatus"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("chargingStatus")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("networkSsid") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <span>Network SSID</span>
                <ColumnSortButton
                  label="Network SSID"
                  isActive={sortField === "networkSsid"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("networkSsid")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("hasIp") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="IP"
                  isOpen={openColumnFilter === "hasIp"}
                  isActive={isHasIpFilterActive}
                  onToggle={() => toggleColumnFilter("hasIp")}
                >
                  <DropdownFilterPanel
                    value={deviceFilters.hasIp}
                    options={DEVICE_HAS_IP_OPTIONS}
                    onChange={(values) => setDropdownFilterValue("hasIp", values)}
                    onClear={() => setDropdownFilterValue("hasIp", [])}
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="IP"
                  isActive={sortField === "hasIp"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("hasIp")}
                />
              </div>
            </th>
          )}
          {isAllDevicesView && activeManagedColumnKeys.includes("folderPath") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <span>Folder Path</span>
                <ColumnSortButton
                  label="Folder Path"
                  isActive={sortField === "folderPath"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("folderPath")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("currentFolder") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="Current Folder"
                  isOpen={openColumnFilter === "currentFolder"}
                  isActive={isCurrentFolderFilterActive}
                  onToggle={() => toggleColumnFilter("currentFolder")}
                >
                  <DropdownFilterPanel
                    value={deviceFilters.currentFolder}
                    options={currentFolderFilterOptions}
                    onChange={(values) => setDropdownFilterValue("currentFolder", values)}
                    onClear={() => setDropdownFilterValue("currentFolder", [])}
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="Current Folder"
                  isActive={sortField === "currentFolder"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("currentFolder")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("notes") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider min-w-[12rem]">
              <div className="inline-flex items-center gap-1">
                <span>Notes</span>
                <ColumnSortButton
                  label="Notes"
                  isActive={sortField === "notes"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("notes")}
                />
              </div>
            </th>
          )}
          {activeOptionalTimeColumns.map((column) => (
            <th
              key={column.key}
              className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider"
            >
              {column.label}
            </th>
          ))}
          {activeOptionalPayloadCacheColumns.map((column) => (
            <th
              key={column.key}
              className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider"
            >
              <div className="inline-flex items-center gap-1">
                <span>{column.label}</span>
                <ColumnSortButton
                  label={column.label}
                  isActive={sortField === column.key}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle(column.key)}
                />
              </div>
            </th>
          ))}
          {activeManagedColumnKeys.includes("inKiosk") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="In Kiosk"
                  isOpen={openColumnFilter === "inKiosk"}
                  isActive={isInKioskFilterActive}
                  onToggle={() => toggleColumnFilter("inKiosk")}
                >
                  <DropdownFilterPanel
                    value={deviceFilters.inKiosk}
                    options={DEVICE_IN_KIOSK_OPTIONS}
                    onChange={(values) => setDropdownFilterValue("inKiosk", values)}
                    onClear={() => setDropdownFilterValue("inKiosk", [])}
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="In Kiosk"
                  isActive={sortField === "inKiosk"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("inKiosk")}
                />
              </div>
            </th>
          )}
          {activeManagedColumnKeys.includes("status") && (
            <th className="py-4 px-4 text-xs font-bold text-on-surface-variant uppercase tracking-wider text-center">
              <div className="inline-flex items-center gap-1">
                <ColumnFilterControl
                  label="Status"
                  isOpen={openColumnFilter === "status"}
                  isActive={isStatusFilterActive}
                  onToggle={() => toggleColumnFilter("status")}
                >
                  <DropdownFilterPanel
                    value={deviceFilters.status}
                    options={DEVICE_STATUS_OPTIONS}
                    onChange={(values) => setDropdownFilterValue("status", values)}
                    onClear={() => setDropdownFilterValue("status", [])}
                    onDone={closeColumnFilters}
                  />
                </ColumnFilterControl>
                <ColumnSortButton
                  label="Status"
                  isActive={sortField === "status"}
                  direction={sortDirection}
                  onClick={() => handleColumnSortToggle("status")}
                />
              </div>
            </th>
          )}
        </tr>
      </thead>
      <tbody className="text-sm">
        {isLoading && (
          <tr className="bg-surface-container-lowest animate-pulse">
            <td className="py-3 px-4">
              <div className="h-4 w-4 bg-surface-container-high rounded"></div>
            </td>
            <td className="py-3 px-4">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-surface-container-high rounded-full mr-3"></div>
                <div>
                  <div className="h-3 w-20 bg-surface-container-high rounded mb-1"></div>
                  <div className="h-2 w-32 bg-surface-container-high rounded opacity-50"></div>
                </div>
              </div>
            </td>
            <td className="py-3 px-4">
              <div className="h-3 w-16 bg-surface-container-high rounded"></div>
            </td>
            <td className="py-3 px-4">
              <div className="h-3 w-12 bg-surface-container-high rounded"></div>
            </td>
            <td className="py-3 px-4">
              <div className="h-3 w-24 bg-surface-container-high rounded opacity-40"></div>
            </td>
            <td className="py-3 px-4">
              <div className="h-5 w-16 bg-surface-container-high rounded-full mx-auto"></div>
            </td>
          </tr>
        )}
        {!isLoading && fetchError && (
          <tr>
            <td colSpan={tableColSpan} className="py-6 px-4 text-center text-error font-semibold">
              {fetchError}
            </td>
          </tr>
        )}
        {!isLoading && !fetchError && displayedDevices.length === 0 && (
          <tr>
            <td colSpan={tableColSpan} className="py-6 px-4 text-center text-on-surface-variant">
              {totalDevices === 0 ? "No devices found" : "No devices match your filters"}
            </td>
          </tr>
        )}
        {!isLoading &&
          !fetchError &&
          displayedDevices.map((device) => {
            const status = getStatus(device.isConnected);
            const batteryLevel = device?.deviceInfo?.batteryLevel;
            const ssid = cleanSsid(device?.deviceInfo?.SSID);
            const model =
              device?.deviceInfo?.deviceModel ||
              device?.deviceInfo?.deviceManufacturer ||
              `${device.retailerName} / ${device.branchName}`;
            const isNotAllowed = isDeviceNotAllowed(device);
            const notInKiosk = !isDeviceInKiosk(device);
            const isBrown = isNotAllowed && notInKiosk;
            const useWhiteText = isNotAllowed || isBrown;
            const mutedTextClass = useWhiteText ? "text-white/80" : "text-on-surface-variant";
            const primaryTextClass = useWhiteText ? "text-white" : "text-on-surface";
            let rowClassName =
              "bg-surface-container-lowest hover:bg-surface-container-high transition-colors group";
            if (isBrown) {
              rowClassName = "bg-amber-800 text-white hover:bg-amber-900 transition-colors group";
            } else if (isNotAllowed) {
              rowClassName = "bg-red-600 text-white hover:bg-red-700 transition-colors group";
            } else if (notInKiosk) {
              rowClassName = "bg-pink-200 hover:bg-pink-300 transition-colors group";
            }
            return (
              <tr
                key={`${device.retailerName}-${device.branchName}-${device.deviceId}`}
                className={rowClassName}
              >
                <td className="py-3 px-4 rounded-l-lg border-y border-transparent">
                  <input
                    checked={Boolean(selectedDevices[device.deviceId])}
                    onClick={(e) => handleDeviceSelectionClick(e, device.deviceId)}
                    className="rounded border-outline-variant text-primary focus:ring-primary/20 h-4 w-4"
                    type="checkbox"
                  />
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <span
                      className={`material-symbols-outlined mr-3 transition-colors ${isNotAllowed ? "text-white" : "text-on-surface-variant group-hover:text-primary"}`}
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      devices
                    </span>
                    <div>
                      <button
                        type="button"
                        className={`font-semibold hover:underline text-left ${primaryTextClass}`}
                        onClick={() => openDeviceInfoJsonModal(device)}
                      >
                        {device.deviceId}
                      </button>
                      <p className={`text-[10px] ${mutedTextClass}`}>{model}</p>
                    </div>
                  </div>
                </td>
                {activeManagedColumnKeys.includes("agentCheckIn") && (
                  <td className={`py-3 px-4 ${mutedTextClass}`}>
                    {formatTime(device?.deviceInfo?.timestamp)}
                  </td>
                )}
                {activeManagedColumnKeys.includes("battery") && (
                  <td className="py-3 px-4">
                    <BatteryLevelCell
                      level={batteryLevel}
                      getBatteryIcon={getBatteryIcon}
                      considerPluggedStatus
                      isPlugged={readDeviceIsPlugged(device)}
                      isNotAllowed={isNotAllowed}
                    />
                  </td>
                )}
                {activeManagedColumnKeys.includes("chargingStatus") && (
                  <td className={`py-3 px-4 ${mutedTextClass}`}>
                    {formatNullableBoolean(readDeviceIsPlugged(device))}
                  </td>
                )}
                {activeManagedColumnKeys.includes("networkSsid") && (
                  <td className={`py-3 px-4 font-mono text-xs ${mutedTextClass}`}>{ssid}</td>
                )}
                {activeManagedColumnKeys.includes("hasIp") && (
                  <td className={`py-3 px-4 ${mutedTextClass}`}>
                    {deviceHasIpOnRecord(device) ? "True" : "False"}
                  </td>
                )}
                {isAllDevicesView && activeManagedColumnKeys.includes("folderPath") && (
                  <td className={`py-3 px-4 text-xs ${mutedTextClass}`}>
                    {getDuplicateFolderPath(device.deviceId) || "-"}
                  </td>
                )}
                {activeManagedColumnKeys.includes("currentFolder") && (
                  <td className={`py-3 px-4 text-xs ${mutedTextClass}`}>
                    {getCurrentFolderName(device.deviceId) || "-"}
                  </td>
                )}
                {activeManagedColumnKeys.includes("notes") && (
                  <td className="py-3 px-4 align-top max-w-[16rem]">
                    <DeviceNotesCell
                      deviceId={device.deviceId}
                      notes={readDevicePayloadField(device, ["notes"])}
                      canEdit={canEditDeviceNotes}
                      isSaving={Boolean(savingNotesDeviceIds?.[device.deviceId])}
                      onSave={onDeviceNotesSave}
                      isNotAllowed={isNotAllowed}
                    />
                  </td>
                )}
                {activeOptionalTimeColumns.map((column) => (
                  <td
                    key={`${device.deviceId}-${column.key}`}
                    className={`py-3 px-4 text-xs whitespace-nowrap ${mutedTextClass}`}
                  >
                    {formatTime(deviceActionResults?.[device.deviceId]?.[column.actionKey]?.updated_at)}
                  </td>
                ))}
                {activeOptionalPayloadCacheColumns.map((column) => (
                  <td
                    key={`${device.deviceId}-${column.key}`}
                    className={
                      column.valueKind === "battery"
                        ? "py-3 px-4 whitespace-nowrap"
                        : column.valueKind === "locationVersionUpdated" ||
                            column.valueKind === "ncrStatus"
                          ? `py-3 px-4 text-xs whitespace-pre-line max-w-[14rem] ${mutedTextClass}`
                        : `py-3 px-4 text-xs whitespace-nowrap max-w-[14rem] truncate ${mutedTextClass}`
                    }
                    title={formatOptionalPayloadCacheColumnValue(device, column)}
                  >
                    {column.valueKind === "battery" ? (
                      <BatteryLevelCell
                        level={readDevicePayloadField(device, column.payloadKeys)}
                        getBatteryIcon={getBatteryIcon}
                        isNotAllowed={isNotAllowed}
                      />
                    ) : (
                      formatOptionalPayloadCacheColumnValue(device, column)
                    )}
                  </td>
                ))}
                {activeManagedColumnKeys.includes("inKiosk") && (
                  <td className={`py-3 px-4 ${mutedTextClass}`}>
                    {isDeviceInKiosk(device) ? "True" : "False"}
                  </td>
                )}
                {activeManagedColumnKeys.includes("status") && (
                  <td className="py-3 px-4 rounded-r-lg text-center">
                    <span
                      className={
                        isNotAllowed
                          ? "px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white"
                          : `px-2 py-0.5 rounded-full text-[10px] font-bold ${status.classes}`
                      }
                    >
                      {status.label}
                    </span>
                  </td>
                )}
              </tr>
            );
          })}
      </tbody>
    </table>
  </div>
  );
};

export default MdmDevicesTable;
